/** @prettier */

import {useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import {useTranslation} from 'react-i18next';

import {invertComma} from '../functions/convertString';
import {specialID} from '../define';

/**
 * 小数点の桁の設定
 * @param {number} value 桁数を変更したい数値
 * @returns valueの小数点以下の桁数を1にした数
 */
const valueText = (value: number) => {
  return value.toFixed(1);
};

/**
 * 機器操作　設定温度スライダー
 * @param props props
 * - presetTemp 表示する設定温度
 * - operationMode 設定している運転モード
 * - setOperationTemp　設定温度指令 設定関数
 * @returns JSX
 */
const PresetTempSlider = (props: {
  displayPresetTemp: number;
  operationMode:
    | 'auto'
    | 'dehumidify'
    | 'cooling'
    | 'fan'
    | 'heating'
    | undefined;
  setOperationTemp: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const trans = useTranslation().t; //翻訳関数

  const {displayPresetTemp, operationMode, setOperationTemp} = props;

  /** 冷房時ラベル温度 */
  const marksCOOL = [
    {
      value: 18,
      label: `18,0${trans('DiscreteSlider.unit')}`,
    },
    {
      value: 24,
      label: `24,0${trans('DiscreteSlider.unit')}`,
    },
    {
      value: 30,
      label: `30,0${trans('DiscreteSlider.unit')}`,
    },
  ];
  /** 暖房時ラベル温度 */
  const marksHOT = [
    {
      value: 16,
      label: `16,0${trans('DiscreteSlider.unit')}`,
    },
    {
      value: 24,
      label: `24,0${trans('DiscreteSlider.unit')}`,
    },
    {
      value: 30,
      label: `30,0${trans('DiscreteSlider.unit')}`,
    },
  ];

  //表示する設定温度
  const [value, setValue] = useState<number>(props.displayPresetTemp);

  useEffect(() => {
    setValue(displayPresetTemp);
    setOperationTemp(displayPresetTemp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayPresetTemp]);

  //暖房18°C未満の状態から、暖房以外に切り替えたとき、18°Cにする
  useEffect(() => {
    if (operationMode !== 'heating') {
      if (value < 18) {
        setValue(18);
        setOperationTemp(18);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationMode]);

  /**
   * 設定温度指令値の更新
   * @param {Event} event スライダーの移動
   * @param {number | number[]} newValue 設定温度
   */
  const handleChange = (event: Event, newValue: number | number[]) => {
    if (typeof newValue === 'number') {
      setValue(newValue);
      setOperationTemp(newValue);
    }
  };

  return (
    <Stack spacing={2}>
      <Box className="operation__card__content__setting__value__currentValue--temperature">
        {invertComma(valueText(value))}
        <Box
          component="span"
          className="operation__card__content__setting__value__currentValue__degC">
          &nbsp;{trans('DiscreteSlider.unit')}
        </Box>
      </Box>
      <Box className="operation__card__content__setting__value__slider">
        <Slider
          id={specialID.IGNORE_INTERACTJS_ID}
          aria-label="Temperature"
          value={value}
          getAriaValueText={valueText}
          valueLabelDisplay="auto"
          step={0.5}
          marks={props.operationMode === 'heating' ? marksHOT : marksCOOL}
          min={props.operationMode === 'heating' ? 16 : 18}
          max={30}
          onChange={handleChange}
        />
      </Box>
    </Stack>
  );
};

export default PresetTempSlider;
